import { merge } from "lodash-es";
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { debugValue } from "@/env/utils";
import { Capacitor } from "@/lib/capacitor";
import { createSelectors } from "@/lib/store/utils/create-selectors";

export type ModalKeys = "appExplainer" | "pushNotificationsDenied";

type BaseModalState = { isOpen: boolean };

interface ModalStates extends Record<ModalKeys, BaseModalState> {
  coreValuesDetail: BaseModalState & {
    item: {
      name?: string | null;
      reason?: string | null;
      score?: number | null;
      type?: "friend-check" | "reflection" | null;
    } | null;
  };
  coreValuesPersonalizeCompleted: BaseModalState & { id: string | null };
  reflectionExplainerGeneral: BaseModalState & { onCompleted: VoidFunction | null };
  reflectionExplainerFirstQuestion: BaseModalState & { id: string | null };
  reflectionChatCompleted: BaseModalState & { id: string | null };
  reflectionChatFirstAnswer: BaseModalState & { id: string | null };
  cropProfilePicture: BaseModalState & {
    imageUrl: string;
    onCompleted: ((crop: string) => void) | null;
  };
}

type ModalActions = Record<
  "present" | "dismiss",
  <T extends ModalKeys>(key: T, update?: Partial<ModalStates[T]>) => void
>;

export const useModalStore = create<ModalStates & ModalActions>()(
  devtools(
    immer((set) => ({
      appExplainer: { isOpen: false },
      appFeedback: { isOpen: false },
      coreValuesCompileExplainer: { isOpen: false },
      coreValuesCompileGeneral: { isOpen: false },
      coreValuesPersonalizeExplainer: { isOpen: false },
      coreValuesPersonalizeCompleted: { isOpen: false, id: null },
      coreValuesDetail: { isOpen: false, item: null },
      devDeleteFriendcheck: { isOpen: false },
      friendCheckAnalysisGenerate: { isOpen: false },
      friendCheckAnalysisView: { isOpen: false },
      friendCheckCompleted: { isOpen: false },
      friendCheckExplainer: { isOpen: false },
      reflectionAnalysisGenerate: { isOpen: false },
      reflectionAnalysisView: { isOpen: false },
      reflectionCompleted: { isOpen: false },
      reflectionExplainerGeneral: { isOpen: false, onCompleted: null },
      reflectionExplainerFirstQuestion: { isOpen: false, id: null },
      reflectionChatCompleted: { isOpen: false, id: null },
      reflectionChatFirstAnswer: { isOpen: false, id: null },
      deleteAccount: { isOpen: false },
      cropProfilePicture: { isOpen: false, imageUrl: "", onCompleted: null },
      pushNotificationsDenied: { isOpen: false },
      impolitic: { isOpen: false },

      present: <T extends ModalKeys>(key: T, update?: Partial<ModalStates[T]>) =>
        set((state) => {
          state[key].isOpen = true;

          if (typeof update === "object") {
            state[key] = merge(state[key], update);
          }
        }),
      dismiss: <T extends ModalKeys>(key: T, update?: Partial<ModalStates[T]>) =>
        set((state) => {
          state[key].isOpen = false;

          if (typeof update === "object") {
            state[key] = merge(state[key], update);
          }
        }),
    })),
    {
      enabled: debugValue("store") && Capacitor.Platform === "web",
    },
  ),
);

export const useSelectorModalStore = createSelectors(useModalStore);
